import {Injectable} from '@angular/core';
import {fromEvent, Observable, of} from 'rxjs';
import {filter, map, take, tap} from 'rxjs/operators';
import {APIService} from './api.service';

export interface SSOTokenResponse {
    token: string;
    refresh_token: string;
}

export interface SSOLoginResult {
    success: boolean;
    error?: string;
    token?: string;
    refresh_token?: string;
}

const SSO_RESULT_KEY = 'sso_login_result';
const SSO_POPUP_FLOW_KEY = 'sso_popup_flow';
export const SSO_POPUP_NAME = 'arsana_sso_popup';

@Injectable({
    providedIn: 'root',
})
export class SSOService {
    private ssoWindow: Window | null = null;

    static get isPopupFlow() {
        return sessionStorage.getItem(SSO_POPUP_FLOW_KEY) === 'true';
    }

    openSSOLogin(): Observable<SSOLoginResult> {
        const ssoUrl = APIService.urls.SSOLoginView.get();

        localStorage.removeItem(SSO_RESULT_KEY);

        this.ssoWindow = window.open('', SSO_POPUP_NAME);

        if (!this.ssoWindow) {
            return of({success: false, error: 'Popup was blocked. Please allow popups for this site.'});
        }

        try {
            this.ssoWindow.sessionStorage.setItem(SSO_POPUP_FLOW_KEY, 'true');
        } catch (e) {
            console.error('Could not set SSO popup state', e);
        }

        this.ssoWindow.location.href = ssoUrl;

        return fromEvent<StorageEvent>(window, 'storage').pipe(
            filter(event => event.key === SSO_RESULT_KEY && !!event.newValue),
            map(event => {
                try {
                    return JSON.parse(event.newValue!) as SSOLoginResult;
                } catch {
                    return null;
                }
            }),
            filter((result): result is SSOLoginResult => result !== null),
            take(1),
            tap(() => {
                localStorage.removeItem(SSO_RESULT_KEY);
                this.ssoWindow = null;
            })
        );
    }

    /**
     * Called from the SSO callback page to signal success to the opener tab.
     */
    static notifySuccess(token?: string, refresh_token?: string) {
        const result: SSOLoginResult = {
            success: true,
            token,
            refresh_token,
        };
        localStorage.setItem(SSO_RESULT_KEY, JSON.stringify(result));
    }

    /**
     * Called from the SSO callback page to signal an error to the opener tab.
     */
    static notifyError(error: string) {
        localStorage.setItem(SSO_RESULT_KEY, JSON.stringify({success: false, error}));
    }

    exchangeCode(code: string, state: string): Observable<SSOTokenResponse> {
        return APIService.SSOCallbackView.post({code, state});
    }
}

